import { api } from "../api";
import { CORE_API_HOSTNAME } from "../env";
import type * as AuthApi from "../types/management-auth";

export const fetchAccountUser = async (cognitoToken: string) => {
    const headers = { Authorization: `Bearer ${cognitoToken}` };
    const response: AuthApi.AuthenticatedAccountUser | null =
        await api<AuthApi.AuthenticatedAccountUser | null>({
            url: `${CORE_API_HOSTNAME}/v1/account/user`,
            headers,
            handlers: {
                200: (json) => {
                    return json;
                },
                401: () => {
                    return null;
                },
                403: () => {
                    return null;
                },
            },
        });
    return response;
};

export const fetchAccountToken = async (
    cognitoToken: string,
    accountId: string,
) => {
    const headers = { Authorization: `Bearer ${cognitoToken}` };
    const result = await api<{
        accountId: string;
        token: string;
    } | null>({
        method: "POST",
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/auth/token`,
        json: {
            grant_type: "account_user",
        },
        headers,
        handlers: {
            200: (responseJson: any) => {
                if (responseJson?.access_token) {
                    const token = responseJson.access_token;
                    return {
                        accountId,
                        token,
                    };
                } else {
                    throw new Error(
                        "200 response does not contain access_token",
                    );
                }
            },
            403: () => {
                return null;
            },
        },
    });
    return result;
};

export const fetchPostPassword = async (
    cognitoToken: string,
    payload: {
        previous_password: string;
        proposed_password: string;
    },
) => {
    const headers = { Authorization: `Bearer ${cognitoToken}` };
    // Get account tokens for account, both test and prod if exists
    const response = await api<Response>({
        method: "POST",
        url: `${CORE_API_HOSTNAME}/v1/account/user/password`,
        headers,
        json: payload,
        parseResponse: false,
    });
    return response;
};

export const fetchPutMfaSms = async (
    cognitoToken: string,
    payload: {
        enabled: boolean;
        phone_number?: string;
    },
) => {
    const headers = { Authorization: `Bearer ${cognitoToken}` };
    // Get account tokens for account, both test and prod if exists
    const response = await api<Response>({
        method: "PUT",
        url: `${CORE_API_HOSTNAME}/v1/account/user/mfa/sms`,
        headers,
        json: payload.enabled ? payload : { enabled: payload.enabled },
        parseResponse: false,
    });
    return response;
};

export const fetchPutMfaTotp = async (
    cognitoToken: string,
    payload: {
        enabled: boolean;
        user_code?: string;
    },
) => {
    const headers = { Authorization: `Bearer ${cognitoToken}` };
    const response = await api<Response>({
        method: "PUT",
        url: `${CORE_API_HOSTNAME}/v1/account/user/mfa/totp`,
        headers: headers,
        json: payload.enabled ? payload : { enabled: payload.enabled },
        parseResponse: false,
    });
    return response;
};

export const fetchExchangeToken = async (
    accountId: string,
    subAccountId: string,
    cognitoToken?: string,
) => {
    // Does not need a cognito token parameter as it uses the partner account token
    // via api.ts to get the exchange token.
    const headers = cognitoToken
        ? { Authorization: `Bearer ${cognitoToken}` }
        : undefined;

    const result: {
        access_token: string;
    } | null = await api<{ access_token: string } | null>({
        method: "POST",
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/auth/exchange_token`,
        accountId: headers ? undefined : accountId,
        headers: headers,
        json: {
            account_id: subAccountId,
        },
        handlers: {
            200: (response) => response,
            403: () => null,
        },
        useErrorStore: false,
    });

    return result;
};
